import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Modal, Spin } from 'antd';
import './ChangeRequestExportDropdown.less';
import { Notify } from '../../../services/notificationService';
import {
  DownOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { handleExportDoc } from '../../../services/exportService';
import { useAgentSelector } from '../../../hooks/useAgentStore';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import {
  ChangeRequestExportFormat,
  ChangeRequestJSONExportDepth,
} from '../../../enums/changeRequest';
import DepthSelector, { DepthSelectorRef } from './DepthSelector';

interface ChangeRequestExportDropdownProps {
  setIsLoading: (isLoading: boolean) => void;
  disabled?: boolean;
  pdf: boolean;
  docx: boolean;
  xlsx: boolean;
  json: boolean;
}
const ChangeRequestExportDropdown: React.FC<
  ChangeRequestExportDropdownProps
> = ({ setIsLoading, disabled = false, pdf, docx, xlsx, json }) => {
  const selectorRef = useRef<DepthSelectorRef>(null);
  const { data } = useAgentSelector((state) => state.changeRequest);
  const [exportLoadingStates, setExportLoadingStates] = useState<
    Record<string, boolean>
  >({
    pdf: false,
    docx: false,
    xlsx: false,
    json: false,
  });
  const [showJsonExportModal, setShowJsonExportModal] =
    useState<boolean>(false);
  const [modalPromise, setModalPromise] = useState<{
    resolve: (value: string | null) => void;
    reject: (reason?: unknown) => void;
  } | null>(null);

  const showCustomModal = () => {
    return new Promise((resolve, reject) => {
      setShowJsonExportModal(true);
      setModalPromise({ resolve, reject });
    });
  };

  const setExportLoadingState = useCallback((key: string, value: boolean) => {
    setExportLoadingStates((prev) => {
      const newStates = { ...prev, [key]: value };
      setIsLoading(Object.values(newStates).some(Boolean));
      return newStates;
    });
  }, []);

  const [disabledDepths, setDisabledDepths] = useState<Record<string, boolean>>(
    {
      [ChangeRequestJSONExportDepth.SUMMARY]: false,
      [ChangeRequestJSONExportDepth.CHECKLIST]: true,
      [ChangeRequestJSONExportDepth.EXECUTION]: true,
    }
  );

  const [selectedDepthValues, setSelectedDepthValues] = useState<
    string[] | null
  >(null);

  const resetSelectedDepthValues = () => {
    selectorRef.current?.resetValues();
  };

  useEffect(() => {
    setDisabledDepths((prev) => {
      const noChecklist = !data?.result?.checklist?.items?.length;
      const noExecution = !data?.result?.execution?.items?.length;
      return {
        ...prev,
        [ChangeRequestJSONExportDepth.CHECKLIST]: noChecklist,
        [ChangeRequestJSONExportDepth.EXECUTION]: noExecution,
      };
    });
  }, [data?.result]);

  useEffect(() => {
    resetSelectedDepthValues();
  }, [disabledDepths]);

  const handleExport = async (format: string) => {
    const { id, name } = data?.result || {};

    if (!id || !name) {
      Notify.error('Unable to retrieve Change Request ID.');
      return;
    }

    try {
      if (format === ChangeRequestExportFormat.JSON) {
        const selectedDepth = await showCustomModal();
        if (selectedDepth) {
          setExportLoadingState(format, true);
          await handleExportDoc(format, id, {
            depth: selectedDepth as string,
          });
        }
      } else {
        setExportLoadingState(format, true);
        await handleExportDoc(format, id);
      }
    } catch (error) {
      Notify.error((error as Error).message || 'Export failed!');
    } finally {
      setExportLoadingState(format, false);
      setShowJsonExportModal(false);
    }
  };

  const generateExportMenuItem = ({
    type,
    icon: Icon,
  }: {
    type: string;
    icon: React.ComponentType<{ className?: string }>;
  }) => {
    const isLoading = exportLoadingStates[type];
    return {
      label: (
        <>
          <Icon className={`cr-export-icon export-${type}-icon`} />
          {isLoading ? (
            <Spin size="small" />
          ) : (
            `Export as ${type.toUpperCase()} (.${type})`
          )}
        </>
      ),
      key: type,
      onClick: () => handleExport(type),
      disabled: isLoading,
    };
  };

  const exportMenuItems = [
    pdf &&
      generateExportMenuItem({
        type: ChangeRequestExportFormat.PDF,
        icon: FilePdfOutlined,
      }),
    docx &&
      generateExportMenuItem({
        type: ChangeRequestExportFormat.DOCX,
        icon: FileWordOutlined,
      }),
    xlsx &&
      generateExportMenuItem({
        type: ChangeRequestExportFormat.XLSX,
        icon: FileExcelOutlined,
      }),
    json &&
      generateExportMenuItem({
        type: ChangeRequestExportFormat.JSON,
        icon: FileOutlined,
      }),
  ].filter(Boolean) as ItemType<MenuItemType>[];

  return (
    <div className="change-request-export-dropdown">
      <Modal
        className="change-request-export-dropdown-modal"
        title="Export including...."
        open={showJsonExportModal}
        okButtonProps={{ disabled: !selectedDepthValues?.length }}
        onCancel={() => {
          setExportLoadingState(ChangeRequestExportFormat.JSON, false);
          resetSelectedDepthValues();
          setShowJsonExportModal(false);
          modalPromise?.resolve(null);
        }}
        onOk={() => {
          const selectedDepth = selectedDepthValues as string[];
          const depth = selectedDepth[selectedDepth.length - 1] || null;
          setExportLoadingState(ChangeRequestExportFormat.JSON, false);
          setShowJsonExportModal(false);
          modalPromise?.resolve(depth);
        }}
      >
        <DepthSelector
          ref={selectorRef}
          selectedDepthValues={selectedDepthValues}
          disabledDepths={disabledDepths}
          onChange={setSelectedDepthValues}
        />
      </Modal>
      <Dropdown
        menu={{ items: exportMenuItems }}
        trigger={['hover']}
        disabled={disabled}
      >
        <Button disabled={disabled}>
          Export <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
};

export default ChangeRequestExportDropdown;
